import { TimeUnits } from '@shared/types'
import { convertToMs } from '@shared/utils'
import { useIdleTimer } from 'react-idle-timer'
import { queryClient } from 'v2/configs/rq-setup'
import {
  useMarkInactiveMutation,
  useOrgStatusConfigQuery,
} from 'v2/pages/settings/org-status/queries'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { useAppStore } from 'v2/state/app'
import type { RActiveConversation } from 'v2/types/api/conversations'
import type { ROrgStatus } from 'v2/types/api/settings'
import { conversationQueryKeys, getSettingsQueryKeys } from '../utils/query-key-management'

const DEFAULT_INACTIVE_THRESHOLD_MINUTES = 5

export function useInactivityManager() {
  const { data: statusConfig } = useOrgStatusConfigQuery()
  const { data: profileData } = useProfileQuery()
  const { mutate: markInactive } = useMarkInactiveMutation()
  const isTrackingEnabled = statusConfig?.idle_status?.event_metadata?.enable_tracking?.value
  const humanSpecialistEnabled = profileData?.organizations?.[0]?.human_specialist_enabled ?? false
  const inactivityThreshold =
    statusConfig?.idle_status?.event_metadata?.inactivity?.value ||
    DEFAULT_INACTIVE_THRESHOLD_MINUTES

  const { isIdle } = useIdleTimer({
    timeout: convertToMs(inactivityThreshold, TimeUnits.Minutes),
    throttle: 500,
    crossTab: true,
    disabled: !isTrackingEnabled || !humanSpecialistEnabled,
    name: 'inactivity-tracker',
    onIdle: async () => {
      // extra check to ensure tracking is disabled if there are no active conversations
      const conversations = queryClient.getQueryData<RActiveConversation[]>(
        conversationQueryKeys().active,
      )
      if (conversations?.length) return
      const { agentStatus } = useAppStore.getState()
      const statuses = queryClient.getQueryData<ROrgStatus[]>(getSettingsQueryKeys().status)
      const isAvailable = statuses?.find(
        (s) => s.uuid === agentStatus.uuid && s.availability === true,
      )
      // If the agent is available, mark them as idle
      if (isAvailable) markInactive()
    },
  })

  return { isIdle }
}
